import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

// FX rates mirror the backend FX_TO_USD table (Feb 2026 approximations).
// Each value is: 1 unit of the native currency, expressed in USD.
export const FX_TO_USD = { USD: 1.0, EUR: 1.08, AUD: 0.66, CAD: 0.74, INR: 0.012 };
export const SUPPORTED_CURRENCIES = ["USD", "EUR", "AUD", "CAD", "INR"];
export const CURRENCY_LABELS = {
  USD: "US Dollar",
  EUR: "Euro",
  AUD: "Australian Dollar",
  CAD: "Canadian Dollar",
  INR: "Indian Rupee",
};

const STORAGE_KEY = "ownnest.displayCurrency";
const EXPLICIT_KEY = "ownnest.displayCurrency.explicit";
const FALLBACK_CURRENCY = "INR";

// Any country in the Eurozone maps to EUR.
const EUR_COUNTRIES = new Set([
  "AT","BE","CY","EE","FI","FR","DE","GR","IE","IT","LV","LT","LU","MT",
  "NL","PT","SK","SI","ES","HR",
]);

const COUNTRY_TO_CURRENCY = {
  US: "USD", CA: "CAD", AU: "AUD", NZ: "AUD", IN: "INR",
  // English speaking non-Eurozone -> USD as sensible default
  GB: "USD", IE: "EUR",
};

// Timezone-prefix heuristics — instant, no network, works for the majority.
function detectCurrencyFromTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (!tz) return null;
    if (tz.startsWith("Asia/Kolkata") || tz.startsWith("Asia/Calcutta")) return "INR";
    if (tz.startsWith("Australia/")) return "AUD";
    // Canada timezones live under America/* — enumerate the common ones
    if ([
      "America/Toronto","America/Vancouver","America/Edmonton","America/Winnipeg",
      "America/Halifax","America/St_Johns","America/Regina","America/Montreal",
    ].includes(tz)) return "CAD";
    if (tz.startsWith("America/")) return "USD";
    if (tz.startsWith("Pacific/Auckland")) return "AUD";
    if (tz.startsWith("Europe/")) {
      // London stays USD (GBP not supported), rest of Europe -> EUR
      if (tz === "Europe/London" || tz === "Europe/Isle_of_Man") return "USD";
      return "EUR";
    }
  } catch {}
  return null;
}

function detectCurrencyFromLanguage() {
  try {
    const lang = navigator.language || navigator.languages?.[0] || "";
    const country = lang.split("-")[1]?.toUpperCase();
    if (!country) return null;
    if (EUR_COUNTRIES.has(country)) return "EUR";
    if (COUNTRY_TO_CURRENCY[country]) return COUNTRY_TO_CURRENCY[country];
  } catch {}
  return null;
}

function initialCurrency() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED_CURRENCIES.includes(saved)) return saved;
  } catch {}
  return (
    detectCurrencyFromTimezone() ||
    detectCurrencyFromLanguage() ||
    FALLBACK_CURRENCY
  );
}

const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(initialCurrency);
  const [autoDetected, setAutoDetected] = useState(() => {
    try { return localStorage.getItem(EXPLICIT_KEY) !== "1"; } catch { return true; }
  });
  const geoTried = useRef(false);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, currency); } catch {}
  }, [currency]);

  // Refine via IP geolocation ONCE on first load, only if the user hasn't
  // explicitly picked. ipapi.co is CORS-enabled, no auth required. If it
  // fails or blocks, we keep the timezone/language fallback.
  useEffect(() => {
    if (geoTried.current) return;
    geoTried.current = true;
    let explicit = false;
    try { explicit = localStorage.getItem(EXPLICIT_KEY) === "1"; } catch {}
    if (explicit) return;
    const ctrl = new AbortController();
    (async () => {
      try {
        const resp = await fetch("https://ipapi.co/json/", { signal: ctrl.signal });
        if (!resp.ok) return;
        const data = await resp.json();
        const cc = (data.country_code || data.country || "").toUpperCase();
        let refined = null;
        if (EUR_COUNTRIES.has(cc)) refined = "EUR";
        else if (COUNTRY_TO_CURRENCY[cc]) refined = COUNTRY_TO_CURRENCY[cc];
        if (refined && SUPPORTED_CURRENCIES.includes(refined) && refined !== currency) {
          // Only refine if the user still hasn't picked between mount and now
          try {
            if (localStorage.getItem(EXPLICIT_KEY) === "1") return;
          } catch {}
          setCurrencyState(refined);
          setAutoDetected(true);
        }
      } catch {
        // network blocked or offline — keep the timezone default
      }
    })();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setCurrency = useCallback((c) => {
    if (!SUPPORTED_CURRENCIES.includes(c)) return;
    setCurrencyState(c);
    setAutoDetected(false);
    try { localStorage.setItem(EXPLICIT_KEY, "1"); } catch {}
  }, []);

  const resetToAutoDetected = useCallback(() => {
    try { localStorage.removeItem(EXPLICIT_KEY); } catch {}
    const detected = detectCurrencyFromTimezone() || detectCurrencyFromLanguage() || FALLBACK_CURRENCY;
    setCurrencyState(detected);
    setAutoDetected(true);
  }, []);

  // Convert an amount from the "from" native currency into the currently
  // selected display currency, going via USD.
  const convert = useCallback((amount, from = "USD") => {
    if (amount == null || isNaN(amount)) return 0;
    const src = (from || "USD").toUpperCase();
    const dst = currency;
    const usd = Number(amount) * (FX_TO_USD[src] ?? 1);
    return usd / (FX_TO_USD[dst] ?? 1);
  }, [currency]);

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, resetToAutoDetected, autoDetected, convert }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useDisplayCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useDisplayCurrency must be used inside CurrencyProvider");
  return ctx;
}
